(() => {
  const gallery = document.querySelector('[data-ck-gallery-loading]');
  const root = gallery?.closest('[data-product-root]');
  if (!root || window.casekooLinkooGallery) return;
  const transparent = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
  const tokens = new WeakMap();
  const jobs = new Map();
  const queue = [];
  let running = 0;
  let cover;
  let lastImage;
  let frame;
  let startupReady = false;
  const saveData = navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '');
  const slides = () => [...gallery.querySelectorAll('.feature-media-item')];
  const sameSource = (a, b) => {
    if (!a || !b || a.startsWith('data:')) return false;
    const left = new URL(a, location.href);
    const right = new URL(b, location.href);
    return left.pathname === right.pathname && left.searchParams.get('v') === right.searchParams.get('v');
  };
  const ready = (image) => image?.complete && image.naturalWidth > 1 &&
    !image.hasAttribute('data-src') && !['loading', 'deferred', 'error'].includes(image.dataset.ckImageState);
  const current = () => {
    const list = slides();
    return matchMedia('(max-width: 989px)').matches
      ? gallery.querySelector('.feature-media-item.is-active') || list[0]
      : list.find((slide) => getComputedStyle(slide).display !== 'none') || list[0];
  };
  const clearCover = () => {
    cover?.remove();
    cover = null;
    gallery.removeAttribute('aria-busy');
  };
  const hold = (target) => {
    const image = target?.querySelector('img');
    if (ready(image)) return;
    if (!cover) {
      const displayed = current()?.querySelector('img');
      const previous = ready(displayed) ? displayed : lastImage;
      if (!previous?.currentSrc || previous.naturalWidth < 2) return;
      const track = gallery.querySelector('.splide__track') || gallery;
      if (getComputedStyle(track).position === 'static') track.style.position = 'relative';
      cover = document.createElement('div');
      cover.className = 'ck-gallery-loading-cover';
      cover.style.cssText = 'position:absolute;inset:0;z-index:3;pointer-events:none;background:white;';
      const picture = document.createElement('img');
      picture.src = previous.currentSrc;
      picture.alt = '';
      picture.style.cssText = 'width:100%;height:100%;object-fit:contain;';
      cover.append(picture);
      const status = document.createElement('span');
      status.textContent = 'Loading image…';
      status.setAttribute('role', 'status');
      status.style.cssText = 'position:absolute;bottom:12px;left:50%;transform:translateX(-50%);padding:5px 10px;border-radius:20px;background:rgba(255,255,255,.9);color:#222;font-size:12px;white-space:nowrap;';
      cover.append(status);
      track.append(cover);
    }
    gallery.setAttribute('aria-busy', 'true');
    // Use the target's already loaded small image while its full image is pending.
    const thumb = root.querySelector(`.product-thumbnail-list-item[data-media-id="${CSS.escape(target?.dataset.mediaId || '')}"] img`);
    if (ready(thumb) && sameSource(thumb.currentSrc, image?.dataset.src || image?.dataset.ckDesiredSource)) {
      cover.querySelector('img').src = thumb.currentSrc;
    }
  };
  const pump = () => {
    while (running < 2 && queue.length) {
      const job = queue.shift();
      if (!job.valid()) { jobs.delete(job.key); job.resolve(false); continue; }
      running++;
      job.run().finally(() => { running--; pump(); });
    }
  };
  const prepare = (image, source, sourceSet, priority, valid) => {
    const sizes = image.sizes || '(min-width: 990px) 45vw, 100vw';
    const key = `${sourceSet || source}|${sizes}`;
    const existing = jobs.get(key);
    if (existing) {
      existing.valid = valid;
      if (priority === 'high' && existing.start) existing.start();
      return existing.promise;
    }
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    const job = { key, promise, resolve, valid, started: false };
    job.run = async () => {
      if (job.started) return;
      job.started = true;
      const buffered = new Image();
      buffered.decoding = 'async';
      buffered.fetchPriority = priority;
      buffered.sizes = sizes;
      if (sourceSet) buffered.srcset = sourceSet;
      buffered.src = source;
      try {
        await buffered.decode();
        resolve(buffered);
      } catch {
        jobs.delete(key);
        resolve(false);
      }
      // Bound decoded image retention across repeated color/device changes.
      if (jobs.size > 16) {
        const oldest = jobs.keys().next().value;
        if (oldest !== key) jobs.delete(oldest);
      }
    };
    job.start = () => {
      const index = queue.indexOf(job);
      if (index >= 0) queue.splice(index, 1);
      return job.run();
    };
    jobs.set(key, job);
    if (priority === 'high') job.start();
    else { queue.push(job); pump(); }
    return promise;
  };
  const swap = (image, attributes, priority = 'low') => {
    const token = (tokens.get(image) || 0) + 1;
    tokens.set(image, token);
    image.dataset.ckDesiredSource = attributes.src;
    image.dataset.ckImageState = 'loading';
    if (priority === 'high') schedule();
    const valid = () => tokens.get(image) === token;
    return prepare(image, attributes.src, attributes.srcset, priority, valid).then((buffered) => {
      if (!valid()) return false;
      if (!buffered) {
        image.dataset.ckImageState = 'error';
        if (cover) cover.querySelector('[role="status"]').textContent = 'Image unavailable. Please try again.';
        return false;
      }
      image.loading = 'eager';
      image.fetchPriority = priority;
      if (attributes.srcset) image.srcset = attributes.srcset;
      else image.removeAttribute('srcset');
      image.src = buffered.currentSrc || attributes.src;
      for (const name of ['alt', 'width', 'height']) {
        if (attributes[name] != null) image.setAttribute(name, attributes[name]);
      }
      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-casekoo-gallery-lazy');
      image.dataset.ckImageState = 'ready';
      schedule();
      return true;
    });
  };
  const defer = (image, source, sourceSet, alt) => {
    if (sameSource(image.dataset.ckDesiredSource || image.currentSrc, source)) return;
    tokens.set(image, (tokens.get(image) || 0) + 1);
    image.dataset.src = source;
    image.dataset.srcset = sourceSet;
    image.dataset.ckDesiredSource = source;
    image.dataset.casekooGalleryLazy = '';
    image.dataset.ckImageState = 'deferred';
    image.alt = alt || '';
    image.removeAttribute('srcset');
    image.src = transparent;
  };
  const hydrate = (slide, priority) => {
    const image = slide?.querySelector('img[data-src]');
    if (!image) return;
    swap(image, { src: image.dataset.src, srcset: image.dataset.srcset }, priority);
  };
  const warm = (slide = current(), urgent = false) => {
    const list = slides();
    const index = list.indexOf(slide);
    if (index < 0) return;
    hydrate(slide, urgent ? 'high' : 'low');
    list.slice(index + 1, index + (saveData ? 2 : 4)).forEach((next) => hydrate(next, 'low'));
  };
  const sync = () => {
    frame = null;
    const slide = current();
    const image = slide?.querySelector('img');
    if (ready(image)) {
      lastImage = { currentSrc: image.currentSrc, naturalWidth: image.naturalWidth };
      clearCover();
    } else if (startupReady) hold(slide);
    if (startupReady) warm(slide, true);
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(sync);
  };
  const bind = (attempt = 0) => {
    const instance = window.slideshows?.[gallery.id];
    if (!instance) {
      if (attempt < 100) setTimeout(() => bind(attempt + 1), 100);
      return;
    }
    instance.on('move', (index) => {
      const target = slides()[index];
      hold(target);
      warm(target, true);
    });
    instance.on('moved', schedule);
    instance.on('drag', () => {
      warm(current(), true);
      const target = current()?.nextElementSibling;
      if (target && !ready(target.querySelector('img'))) hold(target);
    });
    instance.on('dragged', schedule);
  };
  root.addEventListener('pointerdown', (event) => {
    startupReady = true;
    const thumbnail = event.target.closest('.product-thumbnail-list-item');
    const target = thumbnail && slides().find((slide) => slide.dataset.mediaId === thumbnail.dataset.mediaId);
    if (target) { hold(target); warm(target, true); }
    else if (gallery.contains(event.target)) warm(current(), true);
  }, { capture: true, passive: true });
  root.addEventListener('shapes:product:mediachange', (event) => {
    if (!startupReady) return;
    const target = slides().find((slide) => slide.dataset.mediaId === String(event.detail?.mediaId));
    if (target) { hold(target); warm(target, true); }
    schedule();
  }, true);
  gallery.addEventListener('load', schedule, true);
  window.casekooLinkooGallery = {
    root, swap, defer,
    beforeChange() { startupReady = true; hold({ querySelector: () => null, dataset: {} }); },
    changed() { if (startupReady) { warm(slides()[0], true); schedule(); } },
    initial() { schedule(); }
  };
  bind();
  const afterLoad = () => {
    const start = () => { startupReady = true; warm(slides()[0]); schedule(); };
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1200 });
    else setTimeout(start, 400);
  };
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });
  schedule();
})();
